<template>
  <v-dialog :model-value="modelValue" max-width="640" @update:model-value="(v) => emit('update:modelValue', v)">
    <v-card>
      <v-card-title class="text-subtitle-1 font-weight-bold">Merge into another order</v-card-title>
      <v-card-text>
        <p class="merge-orders__intro">
          Moves this order's documents and Benefit Verification pages into the order you pick. Order #{{ orderId }}
          then closes as merged. Empty order details on the kept order are filled from the merged order. This cannot be
          undone.
        </p>
        <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-4">
          {{ errorMessage }}
        </v-alert>
        <v-skeleton-loader v-if="!loaded" type="list-item-two-line@2" />
        <template v-else>
          <div class="merge-orders__section">Open orders for {{ patientName }}</div>
          <v-radio-group v-if="orders.length" v-model="choice" hide-details>
            <DecisionOption
              v-for="order in orders"
              :key="order.id"
              :value="order.id"
              :title="orderTitle(order)"
              :meta="orderMeta(order)"
              :warning="drugWarning(order, 'this order', drugName)"
              :selected="choice === order.id" />
          </v-radio-group>
          <p v-else class="merge-orders__empty">This patient has no other open order to merge into.</p>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn-lh variant="text" :disabled="merging" @click="emit('update:modelValue', false)">Cancel</v-btn-lh>
        <v-btn-lh
          color="primary"
          append-icon="mdi-check"
          :disabled="choice === null || !loaded"
          :loading="merging"
          @click="onMerge">
          Merge
        </v-btn-lh>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from "vue";
import DecisionOption from "@/components/intakeReview/DecisionOption.vue";
import { intakeService } from "@/services/intakeService";
import { attachableOrders, drugWarning, orderMeta, orderTitle } from "@/utils/openOrders";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  orderId: { type: Number, required: true },
  patientId: { type: [String, Number], default: null },
  patientName: { type: String, default: "" },
  // This order's drug, for the non-blocking warning when the order picked names another one.
  drugName: { type: String, default: null },
});
const emit = defineEmits(["update:modelValue", "merged"]);

const choice = ref(null);
const orders = ref([]);
// Orders not read yet is not the same as this patient having none: until the answer is in, claim neither.
const loaded = ref(false);
const merging = ref(false);
const errorMessage = ref("");

const fetchMergeableOrders = async () => {
  if (!props.patientId) {
    errorMessage.value = "This order has no patient yet, so there is no other order to merge into.";
    loaded.value = true;
    return;
  }
  try {
    const rows = await intakeService.getPatientOpenOrders(props.patientId);
    orders.value = attachableOrders(rows).filter((order) => order.id !== props.orderId);
  } catch (e) {
    errorMessage.value = `Could not load this patient's open orders: ${e.message}`;
    orders.value = [];
  } finally {
    loaded.value = true;
  }
};

const onMerge = async () => {
  merging.value = true;
  errorMessage.value = "";
  try {
    const destination = await intakeService.postMergeOrders(props.orderId, choice.value);
    emit("update:modelValue", false);
    emit("merged", destination.id);
  } catch (e) {
    errorMessage.value = `Could not merge this order: ${e.message}`;
  } finally {
    merging.value = false;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return;
    choice.value = null;
    errorMessage.value = "";
    loaded.value = false;
    fetchMergeableOrders();
  }
);
</script>

<style lang="scss" scoped>
.merge-orders__intro {
  color: rgb(var(--v-theme-blue-gray));
  margin-bottom: 16px;
}

.merge-orders__section {
  color: rgb(var(--v-theme-accent));
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.merge-orders__empty {
  color: rgb(var(--v-theme-blue-gray));
}
</style>
